import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { RosterPattern } from '@/components/RosterCalculator'

export interface SavedTemplate {
  _id: string
  name: string
  cycles: Array<{ daysOn: number; daysOff: number }>
  userId: string
  createdAt: string
  updatedAt: string
}

export function useRosterTemplates(userId: string | undefined) {
  const [templates, setTemplates] = useState<SavedTemplate[]>([])
  const [loading, setLoading] = useState(false)

  const fetchTemplates = useCallback(async () => {
    if (!userId) {
      setTemplates([])
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.roster_templates.list({
        filter: { userId },
        sort: { createdAt: -1 },
        limit: 100
      })
      setTemplates(list as SavedTemplate[])
    } catch (error) {
      console.error('Failed to fetch saved templates:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  const saveTemplate = async (name: string, pattern: RosterPattern) => {
    if (!userId) {
      throw new Error('Must be logged in to save templates')
    }
    const now = new Date().toISOString()
    const created = await lumi.entities.roster_templates.create({
      name,
      cycles: pattern.cycles,
      userId,
      createdAt: now,
      updatedAt: now
    })
    await fetchTemplates()
    return created
  }

  const updateTemplate = async (
    id: string,
    changes: { name: string; cycles: Array<{ daysOn: number; daysOff: number }> }
  ) => {
    await lumi.entities.roster_templates.update(id, {
      name: changes.name,
      cycles: changes.cycles,
      updatedAt: new Date().toISOString()
    })
    await fetchTemplates()
  }

  const deleteTemplate = async (id: string) => {
    await lumi.entities.roster_templates.delete(id)
    await fetchTemplates()
  }

  return { templates, loading, saveTemplate, updateTemplate, deleteTemplate, refetch: fetchTemplates }
}
